import { ScheduleEntryState, Task, Timestamp } from '@griply/types';
import {
  createPlannedOccurrenceForDate,
  getDueTaskPlanning,
} from '@griply/utils';
import { isAfter, isSameDay } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight16, CalendarUpcoming16, X16 } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import {
  EntryTypes,
  OverdueContextMenu,
} from 'shared/components/ui/task-detail-checkbox/overdue-context-menu';
import { useToday } from 'shared/contexts/today';
import { InteractionType } from 'shared/types/data-type';

import { useData } from '../../use-data';
import * as Styled from './quick-actions.style';

enum SubMenuOptions {
  Skip = 'skip',
  Fail = 'fail',
  Reschedule = 'reschedule',
}

export type QuickActionsProps = {
  task: Task;
  onComplete: (
    task: Task,
    type?: ScheduleEntryState,
    options?: {
      value?: number;
      increment?: number;
      referenceDate?: Timestamp;
    },
  ) => void;
};

export const QuickActions: React.FC<QuickActionsProps> = ({
  task,
  onComplete,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn, dateFormat } = useData();
  const { submit: updateTask } = useUpdateTask();

  const skipButtonRef = useRef<HTMLButtonElement>(null);
  const failButtonRef = useRef<HTMLButtonElement>(null);
  const rescheduleButtonRef = useRef<HTMLButtonElement>(null);

  const [menuOpen, setMenuOpen] = useState<SubMenuOptions>();
  const onSkip = () => setMenuOpen(SubMenuOptions.Skip);
  const onFail = () => setMenuOpen(SubMenuOptions.Fail);
  const onReschedule = () => setMenuOpen(SubMenuOptions.Reschedule);
  const closeMenu = () => setMenuOpen(undefined);

  const dueDate = useMemo(() => {
    const planning = getDueTaskPlanning(task, {
      today,
      weekStartsOn,
      date: today,
      uncompletedOnly: true,
    });

    const firstDue = planning[0];
    if (
      !firstDue ||
      (isAfter(firstDue.date, today) && !isSameDay(firstDue.date, today))
    ) {
      return undefined;
    }
    return firstDue.date;
  }, [task, today, weekStartsOn]);

  const handleAction = (type: ScheduleEntryState, date: Timestamp) => {
    onComplete(task, type, { referenceDate: date });
    closeMenu();
  };

  const _onReschedule = (date?: Timestamp) => {
    if (!!task.frozenAt || !date || !task.schedules?.length) {
      return;
    }

    const schedules = createPlannedOccurrenceForDate(task.schedules, date, {
      originalDate: dueDate,
      today,
      weekStartsOn,
    });

    updateTask({ id: task.id, schedules });
    closeMenu();
  };

  const _onSkip = () => {
    if (dueDate && !isSameDay(dueDate, today)) {
      onSkip();
      return;
    }

    handleAction(ScheduleEntryState.Skip, today);
  };

  const onSkipDate = (date: Timestamp) =>
    handleAction(ScheduleEntryState.Skip, date);

  const _onFail = () => {
    if (dueDate && !isSameDay(dueDate, today)) {
      onFail();
      return;
    }

    handleAction(ScheduleEntryState.Failure, today);
  };

  const onFailDate = (date: Timestamp) =>
    handleAction(ScheduleEntryState.Failure, date);

  return dueDate ? (
    <Styled.Actions>
      <Button
        onClick={onReschedule}
        size={Sizes.Small}
        variant={Variants.Outlined}
        color={Colors.Inherit}
        startIcon={CalendarUpcoming16}
        preventDrag
        ref={rescheduleButtonRef}
        selected={menuOpen === SubMenuOptions.Reschedule}
      >
        {t('task.detail.actions.reschedule.label')}
      </Button>
      <Button
        ref={skipButtonRef}
        onClick={_onSkip}
        size={Sizes.Small}
        variant={Variants.Outlined}
        color={Colors.Inherit}
        startIcon={ArrowRight16}
        selected={menuOpen === SubMenuOptions.Skip}
        preventDrag
      >
        {t('task.detail.actions.skip.label')}
      </Button>
      <Button
        ref={failButtonRef}
        onClick={_onFail}
        size={Sizes.Small}
        variant={Variants.Outlined}
        color={Colors.Inherit}
        startIcon={X16}
        selected={menuOpen === SubMenuOptions.Fail}
        preventDrag
      >
        {t('task.detail.actions.fail.label')}
      </Button>

      {menuOpen === SubMenuOptions.Skip && (
        <OverdueContextMenu
          name={task.name}
          overdueDate={dueDate}
          type={EntryTypes.Skip}
          onDate={onSkipDate}
          onClose={closeMenu}
          referenceElement={skipButtonRef}
        />
      )}

      {menuOpen === SubMenuOptions.Fail && (
        <OverdueContextMenu
          name={task.name}
          overdueDate={dueDate}
          type={EntryTypes.Failure}
          onDate={onFailDate}
          onClose={closeMenu}
          referenceElement={failButtonRef}
        />
      )}

      {menuOpen === SubMenuOptions.Reschedule && (
        <DatepickerWithOptions
          referenceElement={rescheduleButtonRef}
          interactionType={InteractionType.Task}
          value={dueDate}
          onChange={_onReschedule}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          icon={IconOptions.Calendar}
          onOutside={closeMenu}
          canRemoveValue={false}
        />
      )}
    </Styled.Actions>
  ) : null;
};
